import type { CanvasElement } from '@extapia/shared';
import type { RoughCanvas } from 'roughjs/bin/canvas';
import { drawElement } from './shapes.js';
import type { Viewport } from './viewport.js';

export interface RenderContext {
  ctx: CanvasRenderingContext2D;
  rc: RoughCanvas;
  width: number; // device pixels? no — logical pixels
  height: number;
  dpr: number; // devicePixelRatio
  viewport: Viewport;
  background: string;
  gridSize: number | null;
  theme: 'light' | 'dark';
}

const gridColor = (theme: 'light' | 'dark'): string => (theme === 'dark' ? '#1f2937' : '#e5e7eb');

// Sort by fractional index (z-order)
const byIndex = (a: CanvasElement, b: CanvasElement): number => a.index.localeCompare(b.index);

export const renderScene = (rctx: RenderContext, elements: CanvasElement[]): void => {
  const { ctx, rc, width, height, dpr, viewport, background, gridSize, theme } = rctx;

  // Reset transform, clear, apply DPR scale
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, width * dpr, height * dpr);
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width * dpr, height * dpr);
  ctx.scale(dpr, dpr);

  // Apply viewport transform: zoom + scroll
  ctx.scale(viewport.zoom, viewport.zoom);
  ctx.translate(-viewport.scrollX, -viewport.scrollY);

  // Grid
  if (gridSize && gridSize > 0) {
    drawGrid(ctx, {
      x1: viewport.scrollX,
      y1: viewport.scrollY,
      x2: viewport.scrollX + width / viewport.zoom,
      y2: viewport.scrollY + height / viewport.zoom,
      gridSize,
      color: gridColor(theme),
    });
  }

  // Elements
  const sorted = [...elements].sort(byIndex);
  for (const el of sorted) {
    drawElement(ctx, rc, el);
  }

  ctx.restore();
};

const drawGrid = (
  ctx: CanvasRenderingContext2D,
  opts: { x1: number; y1: number; x2: number; y2: number; gridSize: number; color: string },
): void => {
  const { x1, y1, x2, y2, gridSize, color } = opts;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 0.5;
  ctx.beginPath();

  const startX = Math.floor(x1 / gridSize) * gridSize;
  for (let x = startX; x < x2; x += gridSize) {
    ctx.moveTo(x, y1);
    ctx.lineTo(x, y2);
  }

  const startY = Math.floor(y1 / gridSize) * gridSize;
  for (let y = startY; y < y2; y += gridSize) {
    ctx.moveTo(x1, y);
    ctx.lineTo(x2, y);
  }

  ctx.stroke();
  ctx.restore();
};
